import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import ScheduleReservationPop from './Modal/ScheduleReservationPop';
import StoreBar from '../../components/Common/StoreBar/StoreBar.index';
import './style/Schedule.scss';
import './style/_button.scss';
import './style/ScheduleModal.scss';
import { RootState } from '../../store/reducers';
import { ContentsInfo, FrameInfo, GetScheduleType } from '../../store/reducers/schedule/scheduleGetReducer';
import {
  GET_SCHEDULE_REQUEST,
  GET_SCHEDULE_RESERVATION_CONFIRM_INITIAL,
  GET_SCHEDULE_RESERVATION_CONFIRM_REQUEST,
  GET_SCHEDULE_RESERVATION_DISPATCH,
  SCHEDULE_DISPATCH,
} from '../../store/actions';
import { stateScheduleReservation } from '../../store/reducers/schedule/scheduleReservationStateReducer';
import Header from '../../components/Layouts/MyPage/Header/MyPageHeader.index';
import Footer from '../../components/Layouts/MyPage/Footer/MyPageFooter.index';
import CustomLoading from '../../components/Common/Loading/CustomLoading.index';
import { ReservationConfirmData } from '../../store/reducers/schedule/reservationConfirmReducer';

const Schedule: FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  // 初回マウント判定とスキップフラグ（selectFlag による自動検索抑制用）
  const isInitialMount = useRef(true);
  const [isAllNoData, setIsAllNoData] = useState(true);
  const [programComment, setProgramComment] = useState('');
  /**
   * 日付フォーマット取得ユーティリティ
   * formatType:
   *  1 = 今日（YYYYMMDD）
   *  2 = 今日から -7日（YYYYMMDD）
   *  3 = 今日から +7日（YYYYMMDD）
   * fromDateStr を渡すとその日付を基準に計算する
   */
  const formatDate = (formatType: number, fromDateStr?: string | null) => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const JST_OFFSET = 9 * 60 * 60 * 1000;

    const getJSTDate = (date?: Date) => {
      const base = date ? date.getTime() : Date.now();
      return new Date(base + JST_OFFSET);
    };

    let targetDate: Date;

    if (!fromDateStr) {
      targetDate = getJSTDate();
    } else {
      const year = Number(fromDateStr.slice(0, 4));
      const month = Number(fromDateStr.slice(4, 6)) - 1;
      const day = Number(fromDateStr.slice(6, 8));

      targetDate = getJSTDate(new Date(Date.UTC(year, month, day)));
    }

    if (formatType === 1) {
      // 当前 JST 日付
      targetDate = getJSTDate();
    } else if (formatType === 2) {
      // JST - 7 日付
      targetDate.setUTCDate(targetDate.getUTCDate() - 7);
    } else if (formatType === 3) {
      // JST + 7 日付
      targetDate.setUTCDate(targetDate.getUTCDate() + 7);
    }

    const yyyy = targetDate.getUTCFullYear();
    const mm = pad(targetDate.getUTCMonth() + 1);
    const dd = pad(targetDate.getUTCDate());

    return `${yyyy}${mm}${dd}`;
  };

  /**
   * 表示用日付フォーマット YYYYMMDD -> MM/DD
   */
  const formatShowDate = (date: string | null) => {
    if (!date || date.length !== 8) return date;
    const mm = date.substring(4, 6);
    const dd = date.substring(6, 8);
    return `${mm}/${dd}`;
  };

  /**
   * 表示用時間フォーマット HHMM -> HH:MM
   * 無効な場合はそのまま返す
   */
  const formatShowTime = (time: string | null) => {
    if (!time || time.length !== 4) return time;
    const hh = parseInt(time.substring(0, 2), 10);
    const mm = parseInt(time.substring(2, 4), 10);
    return `${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
  };

  const [reservationPopModal, setReservationPopModal] = useState(false);

  const { isGetScheduleLoaded, getSchedule } = useSelector<RootState, GetScheduleType>(
    (state) => state.schedule.scheduleGet,
  );

  const { scheduleDispatch } = useSelector<RootState, stateScheduleReservation>(
    (state) => state.schedule.scheduleReservationState,
  );

  const { isGetReservationConfirmLoading, isGetReservationConfirmLoaded } = useSelector<
    RootState,
    ReservationConfirmData
  >((state) => state.schedule.getReservationConfirmReducer);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [preOpenDay, setPreOpenDay] = useState(
    scheduleDispatch.imageDate != null && scheduleDispatch.imageDate > formatDate(1)
      ? scheduleDispatch.imageDate
      : formatDate(1),
  );

  const [queryParam, setQueryParam] = useState<{
    clubCode: number | null;
    clubName: string | null;
    programId: string | null;
    imageDate: string | null;
    isSkipSearch: boolean;
    memberId: string | null;
    contractDataList: string | null;
  }>(() => {
    if (!scheduleDispatch.clubId) {
      const shopId = sessionStorage.getItem('shopId');
      if (shopId) {
        return {
          clubCode: Number(shopId),
          clubName: null,
          programId: null,
          imageDate: formatDate(1),
          isSkipSearch: false,
          memberId: sessionStorage.getItem('memberID') || null,
          contractDataList: sessionStorage.getItem('contractDataList') || null,
        };
      }
    }

    return {
      clubCode: scheduleDispatch.clubId,
      clubName: scheduleDispatch.clubName,
      programId: null,
      imageDate: scheduleDispatch.imageDate ?? formatDate(1),
      isSkipSearch: false,
      memberId: sessionStorage.getItem('memberID') || null,
      contractDataList: sessionStorage.getItem('contractDataList') || null,
    };
  });

  useEffect(() => {
    return () => {
      dispatch({
        type: SCHEDULE_DISPATCH,
        clubId: 0,
        clubName: '',
        imageDate: scheduleDispatch.imageDate ?? formatDate(1),
      });
    };
  }, [dispatch]);

  /**
   * スケジュール検索を実行する共通関数
   */
  const searchSchedule = useCallback(() => {
    setProgramComment('');
    if (queryParam.clubCode) {
      dispatch({
        type: GET_SCHEDULE_REQUEST,
        clubCode: queryParam.clubCode,
        imageDate: queryParam.imageDate,
        programId: queryParam.programId,
        memberId: queryParam.memberId,
        contractDataList: queryParam.contractDataList,
        history,
      });
    }
  }, [dispatch, history, queryParam]);

  /**
   * queryParam の変更に応じて検索を実行するエフェクト
   * 初回マウントや skipSearch 時は検索を抑制する
   */
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (queryParam.isSkipSearch) {
      return;
    }
    if (queryParam.clubCode) {
      searchSchedule();
    }
  }, [queryParam, searchSchedule]);

  useEffect(() => {
    if (isGetReservationConfirmLoaded) {
      setReservationPopModal(true);
    }
  }, [isGetReservationConfirmLoaded]);

  const openReservationPop = useCallback(
    (day: string, week: string, contentsInfo: ContentsInfo, frameInfo: FrameInfo) => {
      dispatch({
        type: GET_SCHEDULE_RESERVATION_CONFIRM_REQUEST,
        contentsId: contentsInfo.contentsId,
        memberId: sessionStorage.getItem('memberID'),
        clubId: queryParam.clubCode,
        contentsDate: day,
        startTime: frameInfo.startTime,
        endTime: frameInfo.endTime,
        history,
      });
      dispatch({
        type: GET_SCHEDULE_RESERVATION_DISPATCH,
        contentsId: contentsInfo.contentsId,
        clubId: queryParam.clubCode,
        clubName: queryParam.clubName,
        infoDate: day,
        contentStartTime: frameInfo.startTime,
        contentEndTime: frameInfo.endTime,
        week,
        cancelWaitNotice: contentsInfo.cancelWaitNotice,
        history,
      });
    },
    [dispatch, history, queryParam.clubCode, queryParam.clubName],
  );

  /**
   * プログラム選択変更ハンドラ
   */
  const onChangeProgramId = useCallback((eve: React.ChangeEvent<HTMLInputElement>) => {
    const newProgramId = eve.target.value;
    setQueryParam((prev) => ({ ...prev, programId: newProgramId, isSkipSearch: false }));
  }, []);

  /**
   * プログラム選択変更ハンドラ
   */
  const onChangeSelectProgramId = useCallback((eve: React.ChangeEvent<HTMLSelectElement>) => {
    const newProgramId = eve.target.value;
    setQueryParam((prev) => ({ ...prev, programId: newProgramId, isSkipSearch: false }));
  }, []);

  /**
   * 週移動ボタンから表示日を変更する
   * changType: 1=今週, 2=前の週, 3=次の週
   */
  const changeSearchDay = useCallback(
    (changType: number) => {
      let searchDay = formatDate(changType, queryParam.imageDate);
      const today = formatDate(1); // Get today's date in YYYYMMDD format
      if (changType === 1) {
        if (preOpenDay > today) {
          searchDay = preOpenDay;
        } else {
          searchDay = today;
        }
      }
      // Only update if searchDay is not earlier than today
      if (searchDay >= today) {
        setQueryParam((prev) => ({ ...prev, imageDate: searchDay, isSkipSearch: false }));
      }
    },
    [queryParam.imageDate, preOpenDay],
  );

  /**
   * 店舗選択変更ハンドラ
   * StoreBar コンポーネントから呼ばれる
   */
  const handleClubSelect = useCallback((clubId: number, clubName: string, imageDate: string) => {
    setQueryParam((prev) => ({
      ...prev,
      clubCode: clubId,
      clubName,
      imageDate: imageDate > formatDate(1) ? imageDate : formatDate(1),
      isSkipSearch: false,
    }));
    setPreOpenDay(imageDate > formatDate(1) ? imageDate : formatDate(1));
  }, []);

  const handleSetBelongClubName = useCallback(
    (clubName: string, imageDate: string) => {
      if (!queryParam.clubName) {
        setQueryParam((prev) => ({
          ...prev,
          clubName,
          imageDate: imageDate > formatDate(1) ? imageDate : formatDate(1),
          isSkipSearch: false,
        }));
        setPreOpenDay(imageDate > formatDate(1) ? imageDate : formatDate(1));
      }
    },
    [queryParam.clubName],
  );

  /**
   * 初期データ取得エフェクト
   * - クラブ一覧を取得
   * - clubCode が設定されていればスケジュールを取得
   */
  useEffect(() => {
    setReservationPopModal(false);
    dispatch({
      type: GET_SCHEDULE_RESERVATION_CONFIRM_INITIAL,
      history,
    });
    window.scrollTo(0, 0);
    setProgramComment('');
    if (queryParam.clubCode) {
      dispatch({
        type: GET_SCHEDULE_REQUEST,
        clubCode: queryParam.clubCode,
        imageDate: queryParam.imageDate,
        programId: queryParam.programId,
        memberId: queryParam.memberId,
        contractDataList: queryParam.contractDataList,
        history,
      });
    }
  }, [history, dispatch]);

  /**
   * GET_SCHEDULE の結果から programList を確認し、selectFlag のあるプログラムを初期選択する
   * 既に自動検索フラグがセットされた場合は次の検索をスキップするための制御を行う
   */
  useEffect(() => {
    if (isGetScheduleLoaded && getSchedule?.programList?.length > 0) {
      if (queryParam.clubCode) {
        getSchedule.programList.map((program) => {
          if (program.selectFlag === 1) {
            setQueryParam((prev) => ({ ...prev, programId: program.programId, isSkipSearch: true }));
            setProgramComment(program.programComment);
          }
          return program;
        });
      }
    }
    if (!(getSchedule.scheduleList && getSchedule.scheduleList.length > 0)) {
      return;
    }
    let allNoData = true;
    for (let i = 0; i < getSchedule.scheduleList.length; i += 1) {
      const scheduleInfo = getSchedule.scheduleList[i];
      if (scheduleInfo.contentsList && scheduleInfo.contentsList.length > 0) {
        allNoData = false;
        break;
      }
    }
    setIsAllNoData(allNoData);
  }, [isGetScheduleLoaded, getSchedule]);

  // --- 2. 添加滚动处理逻辑 ---
  useEffect(() => {
    // 只有当数据加载完成且 DOM 渲染后才执行
    if (!isGetScheduleLoaded || !getSchedule.scheduleList) return;

    const scrollArea = document.querySelector('.p-scroll-sticky-area');
    const dayColumns = document.querySelectorAll<HTMLElement>('.p-schedule-grid__day-column');

    // 【Chrome対策】URLバーの影響を検知する透明なトラッカーを設置
    const safeTracker = document.createElement('div');
    // CSSテキストで一括設定（Safari用のプレフィックス含む）
    safeTracker.style.cssText =
      'position: -webkit-sticky; position: sticky; top: 0px; height: 0px; width: 100%; visibility: hidden; pointer-events: none; z-index: -1;';
    // ページの一番上に挿入
    const appRoot = document.getElementById('__next') || document.getElementById('root') || document.body;
    appRoot.prepend(safeTracker);

    // クローン（身代わり）を格納する配列
    const stickyItems: Array<{
      type: 'day' | 'slot';
      original: HTMLElement;
      clone: HTMLElement;
      container: HTMLElement;
      parentDayHeader?: HTMLElement | null;
    }> = [];

    // 1. 本物のヘッダーを複製（クローン）して準備する
    dayColumns.forEach((col) => {
      // --- 日付ヘッダーの複製 ---
      const dayHeader = col.querySelector<HTMLElement>('.p-schedule-grid__day-header');
      if (dayHeader) {
        const clone = dayHeader.cloneNode(true) as HTMLElement;
        clone.style.position = 'fixed';
        clone.style.visibility = 'hidden'; // 初期は隠す
        clone.style.zIndex = '50';
        clone.style.margin = '0';
        clone.setAttribute('aria-hidden', 'true');
        document.body.appendChild(clone);

        stickyItems.push({
          type: 'day',
          original: dayHeader,
          clone,
          container: col,
        });
      }

      // --- タイムスロットヘッダーの複製 ---
      const slotCols = col.querySelectorAll<HTMLElement>('.p-time-slots__column');
      slotCols.forEach((slotCol) => {
        const slotHeader = slotCol.querySelector<HTMLElement>('.p-time-slots__header');
        if (slotHeader) {
          const clone = slotHeader.cloneNode(true) as HTMLElement;
          clone.style.position = 'fixed';
          clone.style.visibility = 'hidden';
          clone.style.zIndex = '40';
          clone.style.margin = '0';
          clone.setAttribute('aria-hidden', 'true');
          document.body.appendChild(clone);

          stickyItems.push({
            type: 'slot',
            original: slotHeader,
            clone,
            container: slotCol,
            parentDayHeader: dayHeader,
          });
        }
      });
    });

    // 2. スクロール時の追従処理
    const updateHeaders = () => {
      // トラッカーの位置を取得し、ChromeのURLバーによる「安全な上部(safeTop)」を動的に計算する
      const safeTrackerRect = safeTracker.getBoundingClientRect();
      const safeTop = Math.max(0, safeTrackerRect.top);

      stickyItems.forEach((item) => {
        const { type, original, clone, container, parentDayHeader } = item;

        // 本物が非表示（07/04のような空の列）の場合はクローンも隠してスキップ
        if (original.offsetWidth === 0) {
          clone.style.visibility = 'hidden';
          return;
        }

        // 本物の要素と親カラムの現在位置を取得
        const containerRect = container.getBoundingClientRect();
        const origRect = original.getBoundingClientRect();
        const { height, width } = origRect;

        let isSticky = false;
        let topPos = 0;

        if (type === 'day') {
          // 画面上部（安全圏）に到達したら固定
          if (containerRect.top < safeTop && containerRect.bottom > safeTop) {
            isSticky = true;
            topPos = safeTop; // URLバーの下に配置

            // 下から押し上げられる処理
            if (containerRect.bottom < safeTop + height) {
              topPos = containerRect.bottom - height;
            }
          }
        } else if (type === 'slot') {
          // 日付ヘッダーのすぐ下（安全圏）に到達したら固定
          const dayHeight = parentDayHeader && parentDayHeader.offsetWidth > 0 ? parentDayHeader.offsetHeight : 0;
          const slotSafeTop = safeTop + dayHeight; // URLバー高さ ＋ 日付ヘッダー高さ

          if (containerRect.top < slotSafeTop && containerRect.bottom > slotSafeTop) {
            isSticky = true;
            topPos = slotSafeTop;

            // 下から押し上げられる処理
            if (containerRect.bottom < slotSafeTop + height) {
              topPos = containerRect.bottom - height;
            }
          }
        }

        // クローンの位置と表示を更新
        if (isSticky) {
          clone.style.visibility = 'visible';
          clone.style.top = `${topPos}px`;
          clone.style.left = `${origRect.left}px`;
          clone.style.width = `${width}px`;
          clone.style.height = `${height}px`;
        } else {
          clone.style.visibility = 'hidden';
        }
      });
    };

    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateHeaders();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    if (scrollArea) {
      scrollArea.addEventListener('scroll', onScroll, { passive: true });
    }
    window.addEventListener('resize', onScroll, { passive: true });

    // 初始执行一次
    updateHeaders();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (scrollArea) {
        scrollArea.removeEventListener('scroll', onScroll);
      }
      // 清理创建的克隆元素
      stickyItems.forEach((item) => {
        if (item.clone && item.clone.parentNode) {
          item.clone.parentNode.removeChild(item.clone);
        }
      });
      // 清理安全追踪器
      if (safeTracker && safeTracker.parentNode) {
        safeTracker.parentNode.removeChild(safeTracker);
      }
    };
  }, [isGetScheduleLoaded, getSchedule]); // 当数据变化时重新绑定逻辑

  return (
    <>
      <Header />
      {/* 店舗選択バー */}
      <StoreBar
        selectedClubId={queryParam.clubCode ?? undefined}
        onClubSelect={handleClubSelect}
        onSetBelongClubName={handleSetBelongClubName}
      />
      <div>
        <CustomLoading isLoading={isGetReservationConfirmLoading || !isGetScheduleLoaded} heightSize={50} isOverall />
        {/* コンテナ内パーツ */}
        <div className="css-fcftey schedulePage schedule">
          <section className="p-title-area p-title-area--padding-y30">
            <p className="c-title u-text-center">予約するプログラムと日時を選択してください</p>
          </section>
          <section className="p-content-area u-gap30 u-pb16">
            <div className="p-content-area__column p-content-area__column--width1360 u-gap30 u-sp-gap0">
              {/* 共有パーツ カテゴリーコンテナ(パソコン用) */}
              <div className="p-category-button u-pc-only">
                {getSchedule.programList.map((category, index) => (
                  <React.Fragment key={`category-${category.programId}`}>
                    <input
                      type="radio"
                      name="category"
                      id={`category-${index}`}
                      className="p-category-button__slot"
                      checked={category.selectFlag === 1}
                      value={category.programId}
                      onChange={onChangeProgramId}
                    />
                    <label htmlFor={`category-${index}`} className="p-category-button__label">
                      {category.programName}
                    </label>
                  </React.Fragment>
                ))}
              </div>
              {/* / 共有パーツ カテゴリーコンテナ(パソコン用) */}

              {/* 共有パーツ カテゴリーコンテナ(モバイル用) */}
              <div className="c-select u-sp-only">
                {getSchedule.programList && getSchedule.programList.length > 0 ? (
                  <select
                    name="store"
                    className="c-select__window"
                    onChange={onChangeSelectProgramId}
                    value={queryParam.programId || ''}
                  >
                    {getSchedule.programList.map((category, index) => (
                      <option key={`category-${category.programId}`} value={category.programId}>
                        {category.programName}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select name="store" className="c-select__window">
                    <option value="">予約可能プログラム存在しません</option>
                  </select>
                )}
              </div>
              {/* / 共有パーツ カテゴリーコンテナ(モバイル用) */}
              {programComment ? (
                <div className="p-expand-container">
                  <input type="checkbox" id="toggle-1" className="p-expand-container__input" />
                  <div className="p-expand-container__content" style={{ whiteSpace: 'pre-line' }}>
                    {programComment}
                  </div>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label htmlFor="toggle-1" className="p-expand-container__label" />
                </div>
              ) : (
                <></>
              )}
            </div>

            {/* 共有パーツ ボタングループ */}
            <div className="p-button-group p-button-group--flex-end p-button-group--sp-center">
              <button
                type="button"
                className="p-schedule-button c-button c-button--primary c-button--small c-button--prev u-gap10"
                aria-label="前の週へ"
                onClick={() => changeSearchDay(2)}
                disabled={queryParam.imageDate !== null && queryParam.imageDate <= preOpenDay}
              >
                前の週へ
              </button>
              <button
                type="button"
                className="p-schedule-button p-schedule-button--small c-button c-button--primary c-button--small"
                aria-label="今日"
                onClick={() => changeSearchDay(1)}
              >
                今日
              </button>
              <button
                type="button"
                className="p-schedule-button c-button c-button--primary c-button--small c-button--next u-gap10"
                aria-label="次の週へ"
                onClick={() => changeSearchDay(3)}
              >
                次の週へ
              </button>
            </div>
            {/* / 共有パーツ ボタングループ */}
          </section>
        </div>

        <div
          className={`p-scroll-sticky-area ${isAllNoData ? 'p-scroll-sticky-area--height-auto' : ''}`}
          ref={scrollContainerRef}
        >
          <div className="p-schedule-grid">
            {getSchedule.scheduleList.map((day, dayIndex) => (
              <div
                key={day.infoDate}
                className={`p-schedule-grid__day-column ${
                  dayIndex === getSchedule.scheduleList.length - 1 ? 'p-schedule-grid__day-column--border-right' : ''
                } 
                ${!day.contentsList || day.contentsList.length === 0 ? 'p-schedule-grid__day-column--pink-gray' : ''}
                `}
              >
                <div
                  className={`p-schedule-grid__day-header ${
                    day.week === '土' ? 'p-schedule-grid__day-header--blue' : ''
                  } ${day.week === '日' ? 'p-schedule-grid__day-header--red' : ''}
                    `}
                >
                  {formatShowDate(day.infoDate)}({day.week})
                </div>
                <div
                  className={`p-time-slots ${
                    !day.contentsList || day.contentsList.length === 0 ? 'p-time-slots--none' : ''
                  }`}
                >
                  {day.contentsList && day.contentsList.length > 0 ? (
                    day.contentsList.map((service, serviceIndex) => (
                      <div
                        key={`column ${day.infoDate}-service-${service.contentsId}`}
                        className="p-time-slots__column"
                      >
                        <div className="p-time-slots__header">
                          <div className="p-time-slots__title">{service.contentsName}</div>
                          <div className="p-time-slots__tool">
                            {service.isAnnex === 1 && (
                              <span className="c-genre-label c-genre-label--large-pc">別館</span>
                            )}
                          </div>
                        </div>
                        <div className="p-time-slots__main">
                          {service.frameList && service.frameList.length > 0 ? (
                            service.frameList.map((slot, slotIndex) => (
                              <button
                                key={`${day.infoDate}-${service.contentsId}-${slot.startTime}`}
                                type="button"
                                className={`p-schedule-item ${
                                  slot.bookedCount === slot.totalCount ? 'p-schedule-item--shadow-none' : ''
                                }`}
                                disabled={slot.isDisabled}
                                onClick={() => openReservationPop(day.infoDate, day.week, service, slot)}
                              >
                                <div
                                  className={`p-schedule-item__time ${
                                    slot.isDisabled || slot.bookedCount === slot.totalCount
                                      ? 'p-schedule-item__time--gray'
                                      : ''
                                  }`}
                                >
                                  {formatShowTime(slot.startTime)}~{formatShowTime(slot.endTime)}
                                </div>
                                <div className="p-schedule-item__group">
                                  {slot.bookedCount === slot.totalCount ? (
                                    <span className="p-schedule-item__sold-out">FULL</span>
                                  ) : (
                                    <span
                                      className={`p-schedule-item__number ${
                                        slot.isDisabled ? 'p-schedule-item__time--gray' : ''
                                      }`}
                                    >
                                      {slot.bookedCount}/{slot.totalCount}
                                    </span>
                                  )}
                                  <span
                                    className={`p-schedule-item__text ${
                                      slot.isDisabled ? 'p-schedule-item__time--gray' : ''
                                    }`}
                                  >
                                    詳細
                                    <img
                                      src={
                                        slot.isDisabled
                                          ? '/images/icons/icon-arrow-right-gray.svg'
                                          : '/images/icons/icon-arrow-right-red.svg'
                                      }
                                      alt="画像"
                                      className="p-schedule-item__icon"
                                    />
                                  </span>
                                </div>
                              </button>
                            ))
                          ) : (
                            <></>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-time-slots__column">
                      <div className="p-time-slots__header">
                        <div className="p-time-slots__title">
                          <></>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}

            <div className="p-blank-space" />
          </div>
          <div className="p-blank-space p-blank-space--horizontal" />
        </div>

        <ScheduleReservationPop
          modalShow={reservationPopModal}
          onModalShow={() => setReservationPopModal(false)}
          onRefresh={searchSchedule}
        />
      </div>
      <Footer />
    </>
  );
};

export default Schedule;
